import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
  useNavigate,
  useSearchParams
} from 'react-router-dom';

import PageLoading from '@/components/Loaders/PageLoading';
import SectionLoader from '@/components/Loaders/SectionLoader';
import { removeAuthDetail } from '@/features/auth/authSlice';
import { saveAuthDetail } from '@/features/auth/authSlice';
import { saveLoading } from '@/features/loading/loadingSlice';
import { saveSectionLoading } from '@/features/loading/sectionLoadingSlice';
import { removeModelTypeDetail } from '@/features/modeType/modelTypeSlice';
import { notify } from '@/helpers/notifications/notify';
import QuickResponses from '@/pages/LiveAgent/QuickResponses';
import RedirectToLogin from '@/pages/RedirectLogin';
import RedirectToSignup from '@/pages/RedirectSignup';
import API from '@/services';

const AgentsPage = lazy(() => import('@/pages/Agents/Agents'));
const AnalyticsPage = lazy(() => import('@/pages/Analytics/Analytics'));
const Chat = lazy(() => import('@/pages/Chat'));
const ChatbotCreate = lazy(() => import('@/pages/Chatbots/Create/Create'));
const ChatbotSettings = lazy(() => import('@/pages/Chatbots/Settings/Settings'));
const AvatarSettings = lazy(() => import('@/pages/Avatar/Settings'));
const ChatbotWhiteLabel = lazy(() => import('@/pages/Chatbots/WhiteLabel/WhiteLabel'));
const ChatLogsPage = lazy(() => import('@/pages/ChatLogs/ChatLogs'));
const Dashboard = lazy(() => import('./Dashboard'));
const Embed = lazy(() => import('@/pages/Embed'));
const Demo = lazy(() => import('@/pages/Demo'));
const DemoEmbed = lazy(() => import('@/pages/DemoEmbed'));
const EmbedAvatar = lazy(() => import('@/pages/EmbedAvatar'));
const AvatarDashboard = lazy(() => import('@/pages/Avatar/AvatarDashboard/AvatarDashboard'));
const CreateNewAvatar = lazy(() => import('@/pages/Avatar/Create'));
const CustomizeAvatar = lazy(() => import('@/pages/Avatar/Customize'));
const ErrorPage = lazy(() => import('@/pages/ErrorPage'));
const ForgotPassword = lazy(() => import('@/pages/Auth/ForgotPassword'));
const MemberChangePassword = lazy(() => import('@/pages/Auth/MemberChangePassword'));
const IntegrationsPage = lazy(() => import('@/pages/Integrations/Integrations'));
const MembershipPage = lazy(() => import('@/pages/Membership/Membership'));
const ShareChatbot = lazy(() => import('@/pages/ShareChatbot/ShareChatbot'));
const SharedUrl = lazy(() => import('@/pages/SharedUrl/SharedUrl'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const ProfilePage = lazy(() => import('@/pages/Profile/Profile'));
const RequestToken = lazy(() => import('@/pages/Auth/RequestToken'));
const ResetPassword = lazy(() => import('@/pages/Auth/ResetPassword'));
const Security = lazy(() => import('@/pages/Security/Security'));
const ShareConversation = lazy(() => import('@/pages/ShareConversation'));
const SignUp = lazy(() => import('@/pages/Auth/SignUp'));
const Login = lazy(() => import('@/pages/Auth/Login'));
const StripeCancel = lazy(() => import('@/pages/Stripe/Cancel'));
const StripeCancelTopUp = lazy(() => import('@/pages/Stripe/CancelTopUp'));
const StripeSuccess = lazy(() => import('@/pages/Stripe/Success'));
const StripeSuccessTopUp = lazy(() => import('@/pages/Stripe/SuccessTopUp'));
const Subscribe = lazy(() => import('@/pages/Subscribe'));
const Tooltips = lazy(() => import('@/pages/Embed/Tooltips'));
const VerifyEmail = lazy(() => import('@/pages/Auth/VerifyEmail'));
const LiveAgentForgotPassword = lazy(() => import('@/pages/AuthLiveAgent/ForgotPassword'));
const LiveAgentRequestToken = lazy(() => import('@/pages/AuthLiveAgent/RequestToken'));
const LiveAgentResetPassword = lazy(() => import('@/pages/AuthLiveAgent/ResetPassword'));
const LiveAgentSignIn = lazy(() => import('@/pages/AuthLiveAgent/LogIn'));
const LiveAgentSignUp = lazy(() => import('@/pages/AuthLiveAgent/SignUp'));
const VerifyAgent = lazy(() => import('@/pages/AuthLiveAgent/VerifyAgent'));
const Conversation = lazy(() => import('@/pages/LiveAgent/Conversation'));
const Insights = lazy(() => import('@/pages/LiveAgent/Insights'));
const Overview = lazy(() => import('@/pages/LiveAgent/Overview'));
const Settings = lazy(() => import('@/pages/LiveAgent/Settings'));
const TeamManagement = lazy(() => import('@/pages/TeamManagement/TeamManagement'));
const AcceptTeamManagementInivitation = lazy(() =>
  import('@/pages/TeamManagement/AcceptTeamManagementInivitation')
);
const BlackFriday = lazy(() => import('@/pages/BlackFriday'));
const Credits1000 = lazy(() => import('@/pages/Credits1000'));

const LiveAgentDashboard = lazy(() => import('./LiveAgentDashboard'));

const AuthRoute = ({ user, children }) => {
  const navigate = useNavigate();
  const shouldRequest = useRef(false);
  const [urlParams] = useSearchParams();
  const dispatch = useDispatch();
  const [authenticated, setAuthenticated] = useState(false);

  const refreshToken = useCallback(
    async (user) => {
      try {
        const { data } = await API.refreshToken();
        dispatch(
          saveAuthDetail({
            access_token: data.access_token,
            user_id: user.id,
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            date_created: user.date_created,
            login_count: user.login_count
          })
        );
      } catch (err) {
        navigate('/log-in');
        dispatch(removeModelTypeDetail());
      }
    },
    [dispatch, navigate]
  );

  const verifySession = useCallback(async () => {
    try {
      shouldRequest.current = true;
      setTimeout(async () => {
        const { data } = await API.getUser();
        refreshToken(data);
        setAuthenticated(true);
      }, 50);
      shouldRequest.current = false;
    } catch (err) {
      shouldRequest.current = false;

      dispatch(removeAuthDetail());
    }
  }, [dispatch, refreshToken]);

  useEffect(() => {
    if (urlParams.get('tier_id')) {
      localStorage.setItem('save_url', window.location.href);
    }
    if (!shouldRequest.current) {
      verifySession();
      shouldRequest.current = true;
    }
  }, [dispatch, urlParams, verifySession]);

  return authenticated ? <Suspense fallback={<PageLoading loading />}>{children}</Suspense> : null;
};

// Ensure that only one loader is active at a time, when a component is loading
const FakeSectionLoader = () => {
  const dispatch = useDispatch();

  dispatch(saveSectionLoading(true));

  return <></>;
};
const FakePageLoader = () => {
  const dispatch = useDispatch();

  dispatch(saveLoading(true));

  return <></>;
};

const NoAuthRoute = ({ user, children }) => {
  return <Suspense fallback={<PageLoading loading />}>{children}</Suspense>;
};

const OldAgentRoute = () => {
  const location = useLocation();
  const path = location.pathname.replace('/live-agent', '');
  let redirect = `https://live-agent.dante-ai.com${path}`;

  if (window.location.origin.includes('staging.dante-ai.com')) {
    redirect = `https://live-agent-staging.dante-ai.com${path}`;
  }

  return window.location.assign(redirect);
};

const PermissionAuth = ({ children, permission }) => {
  const profile = useSelector((state) => state.profile);
  const hasPermission = profile.permissions?.find((ele) => ele.name === permission)?.value;
  const selectedTeam = useSelector((state) => state.teamManagement.selectedTeam);

  if ((selectedTeam.id && hasPermission) || !selectedTeam.id) {
    return <Suspense fallback={<PageLoading loading />}>{children}</Suspense>;
  } else {
    notify('failure', "You don't have permission to access this page.");
    return <Navigate to="/" />;
  }
};

const ProtectedRoute = ({ children, permission }) => {
  const profile = useSelector((state) => state.profile);
  const hasPermission = profile.permissions?.find((ele) => ele.name === permission)?.value;
  const selectedTeam = useSelector((state) => state.teamManagement.selectedTeam);
  const tierType = useSelector((state) => state.tiers.tiers);
  const { chatbots } = useSelector((state) => state.chatbots);

  if (selectedTeam.id && hasPermission) {
    return <Suspense fallback={<PageLoading loading />}>{children}</Suspense>;
  } else if (!selectedTeam.id) {
    if (
      permission !== 'assign_avatar' &&
      permission !== 'create_chatbot' &&
      permission !== 'share_chatbots' &&
      permission !== 'edit_chatbots' &&
      (tierType.type.includes('free') || chatbots?.length === 0)
    ) {
      if (chatbots?.length === 0) {
        notify(
          'cta',
          "No chatbots found. Create one with 'Create Chatbot' button:",
          '/chatbot/new',
          'Create chatbot'
        );
      } else if (
        tierType.type.includes('free') &&
        permission !== 'share_chatbots' &&
        permission !== 'edit_chatbots'
      ) {
        notify('cta', 'Upgrade your plan to use this feature:', '/membership', 'Upgrade');
      }
      return <Navigate to="/" />;
    } else {
      return <Suspense fallback={<PageLoading loading />}>{children}</Suspense>;
    }
  } else {
    notify('failure', "You don't have permission to access this page.");
    return <Navigate to="/" />;
  }
};

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <AuthRoute>
          <Suspense fallback={<FakePageLoader loading />}>
            <Dashboard />
          </Suspense>
        </AuthRoute>
      ),
      errorElement: <ErrorPage />,
      children: [
        {
          path: '',
          element: (
            <Suspense fallback={<FakeSectionLoader loading />}>
              <Chat />
            </Suspense>
          )
        },
        {
          path: '/chatbot/new',
          element: (
            <ProtectedRoute permission="create_chatbot">
              <ChatbotCreate />
            </ProtectedRoute>
          )
        },
        {
          path: '/chatbot/settings',
          element: (
            <ProtectedRoute permission="edit_chatbots">
              <ChatbotSettings />
            </ProtectedRoute>
          )
        },
        {
          path: '/avatar/settings',
          element: (
            <ProtectedRoute permission="assign_avatar">
              <AvatarSettings />
            </ProtectedRoute>
          )
        },
        {
          path: '/avatar/customize',
          element: (
            <Suspense fallback={<FakeSectionLoader loading />}>
              <CustomizeAvatar />
            </Suspense>
          )
        },
        {
          path: '/avatar/new',
          element: (
            <ProtectedRoute permission="create_avatar">
              <CreateNewAvatar />
            </ProtectedRoute>
          )
        },
        {
          path: '/avatar',
          element: (
            <Suspense fallback={<FakeSectionLoader loading />}>
              <AvatarDashboard />
            </Suspense>
          )
        },
        {
          path: '/safety',
          element: (
            <ProtectedRoute permission="edit_safety">
              <Security />
            </ProtectedRoute>
          )
        },
        {
          path: '/insights',
          element: (
            <ProtectedRoute permission="analytics">
              <AnalyticsPage />
            </ProtectedRoute>
          )
        },
        {
          path: '/chat-records',
          element: (
            <ProtectedRoute permission="view_chat_logs">
              <ChatLogsPage />
            </ProtectedRoute>
          )
        },
        {
          path: '/membership',
          element: (
            <Suspense fallback={<FakeSectionLoader loading />}>
              <MembershipPage />
            </Suspense>
          )
        },
        {
          path: '/integrations',
          element: (
            <Suspense fallback={<FakeSectionLoader loading />}>
              <IntegrationsPage />
            </Suspense>
          )
        },
        {
          path: '/agent',
          element: (
            <ProtectedRoute permission="live_agents">
              <AgentsPage />
            </ProtectedRoute>
          )
        },
        {
          path: '/profile',
          element: (
            <Suspense fallback={<FakeSectionLoader loading />}>
              <ProfilePage />
            </Suspense>
          )
        },
        {
          path: '/chatbot/customize',
          element: (
            <ProtectedRoute permission="edit_customization">
              <ChatbotWhiteLabel />
            </ProtectedRoute>
          )
        },

        {
          path: '/share',
          element: (
            <ProtectedRoute permission="share_chatbots">
              <ShareChatbot />
            </ProtectedRoute>
          )
        },
        {
          path: '/team-management/:id',
          element: (
            <Suspense fallback={<FakeSectionLoader loading />}>
              <TeamManagement />
            </Suspense>
          )
        }
      ]
    },
    {
      path: '/sign-in',
      element: (
        <>
          <Navigate to={`/log-in?${new URL(window.location.href).searchParams.toString()}`} />
        </>
      )
    },
    {
      path: '/reset-password',
      element: (
        <NoAuthRoute>
          <ResetPassword />
        </NoAuthRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/verify-email',
      element: (
        <NoAuthRoute>
          <VerifyEmail />
        </NoAuthRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/request-token',
      element: (
        <NoAuthRoute>
          <RequestToken />
        </NoAuthRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/forgot-password',
      element: (
        <NoAuthRoute>
          <ForgotPassword />
        </NoAuthRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/change-password',
      element: (
        <NoAuthRoute>
          <MemberChangePassword />
        </NoAuthRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/v2/sign-up',
      element: (
        <RedirectToSignup>
          <SignUp />
        </RedirectToSignup>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/sign-up',
      element: (
        <RedirectToSignup>
          <SignUp />
        </RedirectToSignup>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/v2/log-in',
      element: (
        <RedirectToLogin>
          <Login />
        </RedirectToLogin>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/log-in',
      element: (
        <RedirectToLogin>
          <Login />
        </RedirectToLogin>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/demo/',
      element: (
        <NoAuthRoute>
          <Demo />
        </NoAuthRoute>
      )
    },
    {
      path: '/embed/demo/',
      element: <DemoEmbed />
    },
    {
      path: '/accept-team-invite',
      element: (
        <NoAuthRoute>
          <AcceptTeamManagementInivitation />
        </NoAuthRoute>
      ),
      errorElement: <ErrorPage />
    },
    // {
    //   path: '/embed',
    //   element: <Embed />,
    //   errorElement: <ErrorPage />,
    //   children: [
    //     {
    //       path: '',
    //       element: <Chat />
    //     }
    //   ]
    // },
    // {
    //   path: '/embed/tooltips',
    //   element: <Tooltips />,
    //   errorElement: <ErrorPage />
    // },
    {
      path: '/stripe/success',
      element: <StripeSuccess />,
      errorElement: <ErrorPage />
    },
    {
      path: '/stripe/cancel',
      element: <StripeCancel />,
      errorElement: <ErrorPage />
    },
    {
      path: '/stripe/topup-success',
      element: <StripeSuccessTopUp />,
      errorElement: <ErrorPage />
    },
    {
      path: '/stripe/topup-cancel',
      element: <StripeCancelTopUp />,
      errorElement: <ErrorPage />
    },
    {
      path: '/subscribe',
      element: (
        <AuthRoute>
          <Suspense fallback={<PageLoading loading />}>
            <Subscribe />
          </Suspense>
        </AuthRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/user/credits-1000',
      element: (
        <AuthRoute>
          <Suspense fallback={<PageLoading loading />}>
            <Credits1000 />
          </Suspense>
        </AuthRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/black-friday',
      element: (
        <AuthRoute>
          <Suspense fallback={<PageLoading loading />}>
            <BlackFriday />
          </Suspense>
        </AuthRoute>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/shared/:id',
      element: (
        <Suspense fallback={<FakeSectionLoader loading />}>
          <SharedUrl />
        </Suspense>
      )
    },
    {
      path: '/share/:id',
      element: <ShareConversation />,
      errorElement: <ErrorPage />
    },
    {
      path: '/pricing',
      element: (
        <>
          <Pricing />
        </>
      ),
      errorElement: <ErrorPage />
    },
    {
      path: '/live-agent/sign-up',
      element: <OldAgentRoute></OldAgentRoute>,
      errorElement: <ErrorPage />
    },
    {
      path: '/live-agent/log-in',
      element: <OldAgentRoute></OldAgentRoute>,
      errorElement: <ErrorPage />
    },
    {
      path: '/live-agent/reset-password',
      element: <OldAgentRoute></OldAgentRoute>,
      errorElement: <ErrorPage />
    },
    {
      path: '/live-agent/forgot-password',
      element: <OldAgentRoute></OldAgentRoute>,
      errorElement: <ErrorPage />
    },
    {
      path: '/live-agent/request-token',
      element: <OldAgentRoute></OldAgentRoute>,
      errorElement: <ErrorPage />
    },
    {
      path: '/live-agent/overview',
      element: <OldAgentRoute></OldAgentRoute>,
      errorElement: <ErrorPage />
    },
    {
      path: '/live-agent/insights',
      element: <OldAgentRoute></OldAgentRoute>
    },
    {
      path: '/live-agent/settings',
      element: <OldAgentRoute></OldAgentRoute>
    },

    {
      path: '/live-agent/quick-responses',
      element: <OldAgentRoute></OldAgentRoute>
    },
    {
      path: '/live-agent/conversation/:id',
      element: <OldAgentRoute></OldAgentRoute>
    },
    {
      path: '/verify-agent-email',
      element: <OldAgentRoute></OldAgentRoute>,
      errorElement: <ErrorPage />
    }
  ],
  {
    basename: '/'
  }
);
const Routers = () => {
  return <RouterProvider router={router} />;
};
export default Routers;
